import { Breadcrumb, Button, Form, Grid, Modal, Select } from "semantic-ui-react";
import React, { Component } from "react";
import { matchPath } from "react-router";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable, computed } from "mobx";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import Link from "~/components/element/Link";
import RouteWrapper from "~/components/wrapper/RouteWrapper";
import ContentLoader from "~/components/element/ContentLoader";
import ToastMessageStore from "~/stores/ToastMessageStore";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import InvoicePageStore from "~/stores/Invoice/InvoicePageStore";
import FormStore from "~/stores/FormStore";
import InvoiceCard from "~/modules/BillingCentral/components/InvoiceCard";
import InvoicePaymentStatus from "~/constants/Invoice/InvoicePaymentStatus";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import CommonHelper from "~/utils/CommonHelper";
import NoCreditCardsMessage from "~/modules/BillingCentral/components/NoCreditCardsMessage";
import NoCreditCardSelectedMessage from "~/modules/BillingCentral/components/NoCreditCardSelectedMessage";
import NoDefaultCreditCardMessage from "~/modules/BillingCentral/components/NoDefaultCreditCardMessage";
import ProviderStore from "~/stores/Provider/ProviderStore";
import CustomerStore from "~/stores/CustomerStore";

@observer
class InvoiceViewPage extends Component {
  @observable invoicePageStore = null;

  constructor(props) {
    super(props);

    const { t } = props;

    document.title = t("billingCentral:invoiceId", { id: this.locationPathnameInvoiceId });

    this.invoicePageStore = new InvoicePageStore();
    this.invoicePageStore.invoiceStore.isLoading = true;

    this.state = {
      confirmationModalVisible: false,
    };
  }

  componentDidMount() {
    const { showError, actualUserStore } = this.props;
    const { invoiceStore, creditCardStore } = this.invoicePageStore;

    invoiceStore.invoiceId = this.locationPathnameInvoiceId;
    invoiceStore.load().catch(error => showError(error.message));

    const { actualUser } = actualUserStore;

    creditCardStore.pagination.itemsPerPage = 100;
    creditCardStore.updateSearchParams({
      currency: actualUser.currency,
    });

    creditCardStore.loadIndex().catch(error => showError(error.message));

    this.bindForm(actualUser);
  }

  @computed
  get locationPathnameInvoiceId() {
    const { location } = this.props;
    const { pathname } = location;

    // React doesn't provide the match params for this case of nested switch
    // We will need to find it by ourselves
    const match = matchPath(pathname, {
      path: "/billing-central/invoices/:invoiceId",
      exact: true,
      strict: false,
    });

    return parseInt(match.params.invoiceId, 10);
  }

  showConfirmationModal = () => this.setState({ confirmationModalVisible: true });

  hideConfirmationModal = () => this.setState({ confirmationModalVisible: false });

  createInvoicePayment() {
    const { t, toastMessageStore, showError } = this.props;
    const { invoicePaymentStore, invoiceStore, creditCardId } = this.invoicePageStore;
    const { invoiceId } = invoiceStore;

    invoicePaymentStore
      .create({ invoiceId, creditCardId })
      .then(() => {
        invoiceStore.load().catch(error => showError(error.message));
        toastMessageStore.addToastMessage(t("billingCentral:invoicePaymentHasBeenCreated"), "positive", t("success"));
      })
      .catch(error => {
        invoiceStore.load();
        showError(error.message);
      });
  }

  bindForm(actualUser) {
    const initialModel = {
      creditCardId: actualUser.defaultCreditCardId !== null ? actualUser.defaultCreditCardId : "",
    };

    this.invoicePageStore.formStore = new FormStore(initialModel);
  }

  render() {
    const { t, toastMessageStore, actualUserStore } = this.props;
    const { confirmationModalVisible } = this.state;
    const { invoiceStore, formStore, invoicePaymentStore, creditCardStore, creditCardOptions } = this.invoicePageStore;
    const { isLoading: invoiceLoading, invoice } = invoiceStore;
    const { model, setProperty, getAggregatedError } = formStore;
    const { creditCardId } = model;
    const { errorMessages: invoicePaymentErrorMessages } = invoicePaymentStore;
    const { creditCards } = creditCardStore;
    const { actualUser } = actualUserStore;

    if (invoiceLoading) {
      return <ContentLoader />;
    }

    const serverValidationErrors = { ...invoicePaymentErrorMessages };

    const validationRules = {
      creditCardId: {
        required: true,
      },
    };
    const validation = getValidationErrors(validationRules, model);

    return (
      <>
        <div className="side-page-outer-container">
          <Breadcrumb>
            <Breadcrumb.Section>
              <Link to="/billing-central">{t("billingCentral:billingCentral")}</Link>
            </Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section>
              <Link to={`/billing-central/invoices?subscriptionId=${invoice.subscriptionId}`}>
                {t("billingCentral:invoices")}
              </Link>
            </Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section active>{t("billingCentral:invoiceId", { id: invoice.invoiceId })}</Breadcrumb.Section>
          </Breadcrumb>

          <div className="side-page-header" style={{ marginBottom: "1rem" }}>
            <div className="side-page-header-grid-wrap">
              <div className="side-page-h1-wrap">
                <h1>{t("billingCentral:invoiceId", { id: invoice.invoiceId })}</h1>
              </div>
            </div>
          </div>

          <Grid centered className="main-grid">
            <Grid.Column>
              {invoiceLoading ? (
                <ContentLoader />
              ) : (
                <>
                  <InvoiceCard invoice={invoice} />
                  {invoice.paymentStatus !== InvoicePaymentStatus.PAID && (
                    <>
                      <Form className="side-page-form-wrap">
                        <div className="side-page-header" style={{ marginBottom: "1rem" }}>
                          <div className="side-page-h2-wrap">
                            <h2>{t("billingCentral:makeAPayment")}</h2>
                          </div>
                        </div>

                        <div className="side-page-input">
                          <h4>{t("billingCentral:creditCard")}</h4>
                          <Form.Field
                            control={Select}
                            name="creditCardId"
                            value={creditCardId}
                            options={creditCardOptions}
                            onChange={(_event, { value }) => setProperty("creditCardId", value)}
                            error={getAggregatedError("creditCardId", validation, serverValidationErrors)}
                            placeholder={`${t("none")}`}
                          />
                        </div>
                      </Form>

                      {creditCards.length === 0 && <NoCreditCardsMessage />}

                      {creditCards.length > 0 && creditCardId === "" && <NoCreditCardSelectedMessage />}

                      {actualUser.defaultCreditCardId === null && <NoDefaultCreditCardMessage />}

                      <div className="side-page-input">
                        <h4>{t("amount")}</h4>
                        <p>{CommonHelper.currencyFormat(invoice.totalAmountWithTaxes - invoice.amountPaid)}</p>
                      </div>

                      <Button
                        color="green"
                        size="large"
                        onClick={() => this.showConfirmationModal()}
                        disabled={!validation.isValid}
                        style={{ marginTop: "2rem" }}
                      >
                        {t("billingCentral:makeAPayment")}
                      </Button>
                    </>
                  )}
                </>
              )}
            </Grid.Column>
          </Grid>
        </div>
        <Modal open={confirmationModalVisible} onClose={() => this.hideConfirmationModal()}>
          <Modal.Header>{t("billingCentral:areYouSureYouWantToMakeThePayment")}</Modal.Header>
          <Modal.Content>
            {t("billingCentral:theAmountWillBeChargedAtTheSelectedCreditCard", {
              amount: CommonHelper.currencyFormat(invoice.totalAmountWithTaxes - invoice.amountPaid),
            })}
          </Modal.Content>
          <Modal.Actions>
            <Button secondary onClick={() => this.hideConfirmationModal()}>
              Cancel
            </Button>
            <Button
              primary
              onClick={() => {
                this.createInvoicePayment();
                this.hideConfirmationModal();
              }}
            >
              Ok
            </Button>
          </Modal.Actions>
        </Modal>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

InvoiceViewPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
  showError: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(ErrorWrapper(RouteWrapper(InvoiceViewPage)));
